/** Shared responsive navigation and content frame for Loomua browser routes. */
import { type ReactNode, useEffect, useRef, useState } from "react";
import { BrandIcon, BrandLogo } from "./brandAssets";
import {
  interfaceLanguageOptions,
  useInterfaceLanguage
} from "./interfaceLanguage";
import {
  readPasswordRecoveryCallback,
  readSocialAuthCallbackError
} from "./auth";
import type { Viewer } from "./routeAccess";
import { Icon, type IconName } from "./icons";
import {
  MotionDrawer,
  MotionFade,
  MotionModal,
  MotionPopover
} from "./motionComponents";
import { Avatar, IconButton, SearchInput } from "./ui";

type ShellIconName =
  | "account"
  | "bell"
  | "bookshelf"
  | "chevron"
  | "collection"
  | "contest"
  | "create"
  | "discover"
  | "document"
  | "follow"
  | "grid"
  | "guide"
  | "home"
  | "idea"
  | "illustration"
  | "latest"
  | "mail"
  | "manga"
  | "menu"
  | "more"
  | "novel"
  | "project"
  | "ranking"
  | "request"
  | "up";

function ShellIcon({ name }: { name: ShellIconName }) {
  const icons: Record<ShellIconName, IconName> = {
    account: "account",
    bell: "bell",
    bookshelf: "bookshelf",
    chevron: "chevron",
    collection: "bookshelf",
    contest: "contest",
    create: "create",
    discover: "discover",
    document: "novel",
    follow: "follow",
    grid: "grid",
    guide: "create",
    home: "home",
    idea: "idea",
    illustration: "illustration",
    latest: "latest",
    mail: "mail",
    manga: "manga",
    menu: "menu",
    more: "more",
    novel: "novel",
    project: "project",
    ranking: "ranking",
    request: "request",
    up: "up"
  };
  return <Icon name={icons[name]} />;
}

type NavItem = {
  href: string;
  icon: ShellIconName;
  label: string;
};

function NavLink({
  item,
  currentPath
}: {
  item: NavItem;
  currentPath: string;
}) {
  const content = (
    <>
      <ShellIcon name={item.icon} />
      <span>{item.label}</span>
    </>
  );

  return (
    <a
      href={item.href}
      aria-current={currentPath === item.href ? "page" : undefined}
    >
      {content}
    </a>
  );
}

function LanguageActionButton({
  language,
  zh,
  onClick
}: {
  language: string;
  zh: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className="app-language-action"
      type="button"
      aria-label={zh ? "切换语言" : "Change language"}
      onClick={onClick}
    >
      {language === "zh-CN" ? "简体中文" : "English"}
    </button>
  );
}

export function AppShell({
  children,
  session,
  onSignOut
}: {
  children: ReactNode;
  session: Viewer;
  onSignOut: () => void | Promise<void>;
}) {
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [headerTucked, setHeaderTucked] = useState(() => window.scrollY > 24);
  const [headerPeeked, setHeaderPeeked] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDetailsElement>(null);
  const authenticated = session.status === "authenticated";
  const { language, setLanguage } = useInterfaceLanguage();
  const zh = language === "zh-CN";
  const currentPath = window.location.pathname;
  const hasAuthCallback =
    readPasswordRecoveryCallback(
      window.location.search,
      window.location.hash
    ) !== undefined ||
    readSocialAuthCallbackError(
      window.location.search,
      window.location.hash
    ) !== undefined;
  const currentTarget = hasAuthCallback
    ? "/"
    : `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const toggleLabel = navigationOpen
    ? zh
      ? "关闭侧栏"
      : "Close sidebar"
    : zh
      ? "打开侧栏"
      : "Open sidebar";
  const navigationItems: NavItem[] = [
    { href: "/", label: zh ? "首页" : "Home", icon: "home" },
    { href: "/search", label: zh ? "发现" : "Discover", icon: "discover" },
    { href: "/bookmarks", label: zh ? "书架" : "Bookshelf", icon: "bookshelf" },
    { href: "/create", label: zh ? "创作" : "Create", icon: "create" },
    { href: "/account", label: zh ? "账户" : "Account", icon: "account" }
  ];
  const headerHidden = headerTucked && !headerPeeked && !navigationOpen;

  useEffect(() => {
    function syncHeaderPosition() {
      const shouldTuck = window.scrollY > 24;
      setHeaderTucked(shouldTuck);
      if (!shouldTuck) setHeaderPeeked(false);
    }

    syncHeaderPosition();
    window.addEventListener("scroll", syncHeaderPosition, { passive: true });
    return () => window.removeEventListener("scroll", syncHeaderPosition);
  }, []);

  useEffect(() => {
    if (!profileMenuOpen && !languageDialogOpen && !navigationOpen) {
      return undefined;
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setProfileMenuOpen(false);
      setLanguageDialogOpen(false);
      setNavigationOpen(false);
    }

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [languageDialogOpen, navigationOpen, profileMenuOpen]);

  useEffect(() => {
    if (!profileMenuOpen) return undefined;

    function closeOnOutsidePointer(event: PointerEvent) {
      if (
        event.target instanceof Node &&
        profileMenuRef.current?.contains(event.target)
      ) {
        return;
      }
      setProfileMenuOpen(false);
    }

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    return () =>
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
  }, [profileMenuOpen]);

  return (
    <div
      className={`app-frame ${navigationOpen ? "has-open-sidebar" : ""} ${
        headerHidden ? "has-tucked-header" : ""
      }`}
      id="top"
    >
      <div
        aria-hidden="true"
        className="app-header-hover-zone"
        onPointerEnter={() => setHeaderPeeked(true)}
      />
      <header
        className="app-header"
        onPointerEnter={() => setHeaderPeeked(true)}
        onPointerLeave={() => {
          if (headerTucked) setHeaderPeeked(false);
        }}
      >
        <div className="app-header-start">
          <IconButton
            className="app-menu-toggle"
            label={toggleLabel}
            aria-controls="app-sidebar"
            aria-expanded={navigationOpen}
            onClick={() => setNavigationOpen((open) => !open)}
          >
            <ShellIcon name="menu" />
          </IconButton>
          <a
            className="app-brand"
            href="/"
            aria-label={zh ? "Loomua 首页" : "Loomua home"}
          >
            <BrandLogo />
            <span className="visually-hidden">loomua</span>
          </a>
        </div>

        <form
          className="app-search"
          action="/search"
          method="get"
          role="search"
        >
          <div className="app-search-control">
            <button
              className="app-search-submit"
              type="submit"
              aria-label={zh ? "提交搜索" : "Submit search"}
            >
              <BrandIcon name="search" />
            </button>
            <SearchInput
              aria-label={zh ? "搜索 Loomua" : "Search Loomua"}
              name="q"
              placeholder={
                zh
                  ? "搜索小说、IP、CP 或创作者"
                  : "Search novels, IPs, ships, or creators"
              }
            />
          </div>
        </form>

        <div className="app-header-actions">
          {authenticated ? (
            <a className="app-publish-action" href="/create">
              <BrandIcon name="create" className="app-publish-icon" />
              {zh ? "发布" : "Publish"}
            </a>
          ) : null}
          {authenticated ? (
            <details
              className={`app-profile-menu ${profileMenuOpen ? "is-open" : ""}`}
              open={profileMenuOpen}
              ref={profileMenuRef}
            >
              <summary
                aria-label={zh ? "个人菜单" : "Profile menu"}
                aria-controls="app-profile-popover"
                aria-expanded={profileMenuOpen}
                onClick={(event) => {
                  event.preventDefault();
                  setProfileMenuOpen((open) => !open);
                }}
              >
                <Avatar name={zh ? "预览读者" : "Preview reader"} />
                <ShellIcon name="chevron" />
              </summary>
              <MotionPopover
                show={profileMenuOpen}
                id="app-profile-popover"
                className="app-profile-popover"
              >
                <div className="app-profile-card">
                  <strong>{zh ? "账户" : "Account"}</strong>
                </div>
                <nav
                  className="app-profile-links"
                  aria-label={zh ? "账户菜单" : "Account menu"}
                >
                  <a href="/account">{zh ? "账户" : "Account"}</a>
                  <a href="/create">{zh ? "创作" : "Create"}</a>
                  <a href="/bookmarks">{zh ? "书签" : "Bookmarks"}</a>
                </nav>
                <LanguageActionButton
                  language={language}
                  zh={zh}
                  onClick={() => {
                    setProfileMenuOpen(false);
                    setLanguageDialogOpen(true);
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setProfileMenuOpen(false);
                    void onSignOut();
                  }}
                >
                  {zh ? "退出登录" : "Sign out"}
                </button>
              </MotionPopover>
            </details>
          ) : (
            <div
              className="app-auth-actions"
              aria-label={zh ? "账户操作" : "Account actions"}
            >
              <LanguageActionButton
                language={language}
                zh={zh}
                onClick={() => setLanguageDialogOpen(true)}
              />
              <a
                className="app-join-action"
                href={`/register?returnTo=${encodeURIComponent(currentTarget)}`}
              >
                {zh ? "加入" : "Join"}
              </a>
            </div>
          )}
        </div>
      </header>

      <MotionModal
        show={languageDialogOpen}
        className="app-language-dialog-layer"
        panelClassName="app-language-dialog"
        labelledBy="app-language-dialog-title"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            setLanguageDialogOpen(false);
          }
        }}
      >
        <header className="app-language-dialog-header">
          <h2 id="app-language-dialog-title">{zh ? "语言" : "Language"}</h2>
          <button
            type="button"
            aria-label={zh ? "关闭语言选择" : "Close language selection"}
            onClick={() => setLanguageDialogOpen(false)}
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>
        <div
          className="app-language-list"
          role="radiogroup"
          aria-label={zh ? "语言" : "Language"}
        >
          {interfaceLanguageOptions.map((option) => (
            <label className="app-language-option" key={option.code}>
              <input
                checked={language === option.code}
                name="app-language-dialog-choice"
                onChange={() => {
                  setLanguage(option.code);
                  setLanguageDialogOpen(false);
                }}
                type="radio"
                value={option.code}
              />
              <span className="app-language-check" aria-hidden="true">
                {language === option.code ? "✓" : ""}
              </span>
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </MotionModal>

      <MotionDrawer
        show={navigationOpen}
        id="app-sidebar"
        className="app-sidebar"
        ariaLabel={zh ? "站点侧栏" : "Site sidebar"}
      >
        <div className="app-sidebar-header">
          <IconButton
            className="app-sidebar-close"
            label={zh ? "关闭侧栏" : "Close sidebar"}
            onClick={() => setNavigationOpen(false)}
          >
            <ShellIcon name="menu" />
          </IconButton>
          <a
            className="app-brand"
            href="/"
            aria-label={zh ? "Loomua 首页" : "Loomua home"}
          >
            <BrandLogo />
            <span className="visually-hidden">loomua</span>
          </a>
        </div>
        <nav aria-label={zh ? "主导航" : "Primary"} className="app-navigation">
          <div className="app-navigation-group">
            {navigationItems.map((item) => (
              <NavLink currentPath={currentPath} item={item} key={item.label} />
            ))}
          </div>
        </nav>
      </MotionDrawer>

      <MotionFade
        show={navigationOpen}
        as="button"
        className="app-sidebar-backdrop"
        ariaLabel={zh ? "关闭侧栏" : "Close sidebar"}
        tabIndex={0}
        onClick={() => setNavigationOpen(false)}
      />
      <div className="app-content">{children}</div>
      <a
        className="app-back-to-top"
        href="#top"
        aria-label={zh ? "回到顶部" : "Back to top"}
      >
        <ShellIcon name="up" />
      </a>
    </div>
  );
}
